'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';

import cx from 'classnames';
import { LocationContext } from 'contexts/LocationContext';
import PropTypes from 'prop-types';

import InlineIntro from 'components/blocks/BlockCardGroup/InlineIntro';
import Lockup from 'components/blocks/shared/Lockup';
import CardRecipe from 'components/cards/CardRecipe';
import ButtonCTA from 'components/ui/ButtonCTA';
import Text from 'components/ui/Text';

import { blockRootProps, getColor } from 'utils';
import { filterRecipes } from 'utils/recipeFiltering';

import PersonalizedRecipesCarousel from './PersonalizedRecipesCarousel';
import {
    contentData,
    getCurrentTimeOfDay,
    getCurrentWeather,
    loadingMessages,
    orderedContentOptions,
} from './contentData';

import styles from './BlockPersonalizedRecipes.module.scss';

const cta = {
    text: 'Explore More',
    style: 'filled-blue-ocean',
    iconType: 'refresh',
    'gtm-label': 'personalized-recipes-explore-more',
};

const inlineIntroPlaceholderCard = {
    __typename: 'InlineIntro',
};

const cardMap = {
    Recipe: CardRecipe,
    InlineIntro,
    Default: null,
};

const BlockPersonalizedRecipes = ({
    __typename,
    anchorId,
    flushTop,
    flushBottom,
    curatedRecipes,
    mostFavoritedIds,
    allRecipes,
    recipesConfig,
    icon,
    headline,
    backgroundColor = 'cream',
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] =
        useState(0);
    const [currentContentIndex, setCurrentContentIndex] = useState(0);
    const [currentContentType, setCurrentContentType] =
        useState('mostFavorited');

    // Resolve mostFavoritedIds -> recipe entries from context, preserving order
    const mostFavoritedResolved = useMemo(() => {
        if (!mostFavoritedIds || mostFavoritedIds.length === 0 || !allRecipes)
            return [];
        const map = new Map(allRecipes.map(r => [r?.sys?.id, r]));
        return mostFavoritedIds.map(id => map.get(id)).filter(Boolean);
    }, [mostFavoritedIds, allRecipes]);

    const [currentRecipes, setCurrentRecipes] = useState(
        mostFavoritedResolved && mostFavoritedResolved.length > 0
            ? mostFavoritedResolved
            : curatedRecipes
    );

    const [isBeginning, setIsBeginning] = useState(true);

    const { locationFallback } = useContext(LocationContext);
    const isLargeQuery = useMedia('(min-width: 1024px)', false);

    // When allRecipes resolves, update to most-favorited if we're in that mode
    useEffect(() => {
        if (
            currentContentType === 'mostFavorited' &&
            mostFavoritedResolved &&
            mostFavoritedResolved.length > 0
        ) {
            setCurrentRecipes(mostFavoritedResolved);
        }
    }, [currentContentType, mostFavoritedResolved]);

    const cardsWithIntro = useMemo(
        () => [inlineIntroPlaceholderCard, ...currentRecipes],
        [currentRecipes]
    );

    // Populate curated content from server props
    if (icon && headline) {
        contentData.curated.icon = icon;
        contentData.curated.headline = headline;
    }

    // Get next content option from a specific index
    const getNextContentOptionFromIndex = async fromIndex => {
        let attempts = 0;
        const maxAttempts = orderedContentOptions.length;

        while (attempts < maxAttempts) {
            const nextIndex =
                (fromIndex + 1 + attempts) % orderedContentOptions.length;
            const nextOption = orderedContentOptions[nextIndex];

            // Handle timeOfDay option - determine specific key based on current time
            if (nextOption.type === 'timeOfDay' && nextOption.key === null) {
                const timeOfDayOption = getCurrentTimeOfDay();
                return {
                    ...timeOfDayOption,
                    index: nextIndex,
                };
            }

            // Handle weather option - determine specific key based on current weather
            if (nextOption.type === 'weather' && nextOption.key === null) {
                const weatherOption = await getCurrentWeather(locationFallback);

                // If weather returns null (no condition matched), skip to next option
                if (weatherOption === null) {
                    attempts++;
                    continue;
                }

                return {
                    ...weatherOption,
                    index: nextIndex,
                };
            }

            // For mostFavorited or other types with a key
            return {
                ...nextOption,
                index: nextIndex,
            };
        }

        // Fallback to curated if all options fail
        return {
            type: 'curated',
            key: null,
            index: 0,
        };
    };

    const handleRefreshClick = async e => {
        e.preventDefault();

        // Prevent multiple clicks
        if (isLoading) return;

        setIsLoading(true);

        // Cycle to next loading message
        setCurrentLoadingMessageIndex(
            prevIndex => (prevIndex + 1) % loadingMessages.length
        );

        // Start minimum delay timer
        const startTime = Date.now();
        const minDelay = 1000; // 1 second minimum

        try {
            let newRecipes = [];
            let newContentType;
            let nextOption;
            let attempts = 0;
            const maxAttempts = orderedContentOptions.length;
            let currentIndex = currentContentIndex; // Track index locally during retries

            // Keep trying until we find content with recipes or exhaust all options
            while (newRecipes.length === 0 && attempts < maxAttempts) {
                // Get next content option in linear order (now async for weather)
                nextOption = await getNextContentOptionFromIndex(currentIndex);

                // Update local index for next iteration
                currentIndex = nextOption.index;

                if (nextOption.type === 'curated') {
                    // Switch to curated
                    newRecipes = curatedRecipes;
                    newContentType = 'curated';
                } else if (nextOption.type === 'favorited') {
                    // Use locally resolved most-favorited list
                    newRecipes = mostFavoritedResolved || [];
                    newContentType = 'mostFavorited';
                } else {
                    // Switch to timeOfDay or weather option
                    const contentCategory = contentData[nextOption.type];
                    const filters = contentCategory[nextOption.key].filters;
                    // Filter recipes client-side using provided data
                    if (!allRecipes || !recipesConfig) {
                        newRecipes = [];
                    } else {
                        newRecipes = filterRecipes(
                            allRecipes,
                            filters,
                            recipesConfig
                        );
                    }
                    newContentType = nextOption.key;
                }

                // If no recipes found and not curated, try next option
                if (newRecipes.length === 0 && nextOption.type !== 'curated') {
                    attempts++;
                } else {
                    // Found recipes or reached curated, break out
                    break;
                }
            }

            // Wait for minimum delay before updating UI
            const elapsed = Date.now() - startTime;
            if (elapsed < minDelay) {
                await new Promise(resolve =>
                    setTimeout(resolve, minDelay - elapsed)
                );
            }

            // Now update the UI state
            setCurrentRecipes(newRecipes);
            setCurrentContentType(newContentType);
            setCurrentContentIndex(nextOption.index);
        } catch (error) {
            console.error('Error refreshing recipes:', error);

            // Still respect minimum delay even on error
            const elapsed = Date.now() - startTime;
            if (elapsed < minDelay) {
                await new Promise(resolve =>
                    setTimeout(resolve, minDelay - elapsed)
                );
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Favorited is already first and supplied by the server; no client fetch needed

    const renderInlineIntroLockup = () => {
        // Determine current content based on type
        const currentContent =
            currentContentType === 'curated'
                ? contentData.curated
                : contentData.timeOfDay[currentContentType] ||
                  contentData.weather[currentContentType] ||
                  contentData.favorited[currentContentType] ||
                  contentData.curated;

        const currentLoadingMessage =
            loadingMessages[currentLoadingMessageIndex];

        return (
            <>
                <div className={styles.lockupContainer}>
                    <div className={styles.lockupInner}>
                        <Lockup
                            icon={currentContent.icon}
                            headline={{
                                text: currentContent.headline,
                            }}
                            theme="Medium"
                        />
                    </div>
                    <div className={styles.ctaContainer}>
                        {/* in desktop the cta should not be accessible if the carousel is not at the beginning */}
                        <ButtonCTA
                            className={styles.ctaDesktop}
                            {...cta}
                            onClick={handleRefreshClick}
                            disabled={isLoading}
                            tabIndex={isBeginning ? 0 : -1}
                        />
                        <ButtonCTA
                            className={styles.ctaMobile}
                            {...cta}
                            onClick={handleRefreshClick}
                            disabled={isLoading}
                        />
                    </div>
                </div>
                <div className={styles.loadingMessage}>
                    <Text
                        baseTheme="displaySmall"
                        themes={{
                            large: 'displayLarge',
                        }}
                    >
                        {currentLoadingMessage.text}
                    </Text>
                </div>
            </>
        );
    };

    return (
        <div
            {...blockRootProps(anchorId, __typename)}
            className={cx(styles.root, {
                [styles.flushTop]: flushTop,
                [styles.flushBottom]: flushBottom,
                [styles.isLoading]: isLoading,
            })}
            style={{
                '--background-color': getColor(backgroundColor),
                '--slider-gap-color': getColor(backgroundColor),
            }}
        >
            <div className={styles.inner}>
                <div className={styles.inlineIntroSmall}>
                    {renderInlineIntroLockup()}
                </div>
                <div className={styles.cardGroupCarousel}>
                    <div className={styles.inlineIntroContent}>
                        <div className={styles.inlineIntroContentInner}>
                            {renderInlineIntroLockup()}
                        </div>
                    </div>
                    <PersonalizedRecipesCarousel
                        className={styles.carousel}
                        cardMap={cardMap}
                        cards={cardsWithIntro}
                        navigationTheme="blue"
                        isLoading={isLoading}
                        onIsBeginning={
                            isLargeQuery ? setIsBeginning : undefined
                        }
                    />
                </div>
            </div>
        </div>
    );
};

BlockPersonalizedRecipes.propTypes = {
    __typename: PropTypes.string,
    anchorId: PropTypes.string,
    flushTop: PropTypes.bool,
    flushBottom: PropTypes.bool,
    curatedRecipes: PropTypes.array,
    mostFavoritedIds: PropTypes.array,
    allRecipes: PropTypes.array,
    recipesConfig: PropTypes.object,
    icon: PropTypes.object,
    headline: PropTypes.object,
    backgroundColor: PropTypes.string,
};

export default BlockPersonalizedRecipes;
